import { useEffect, useRef, useState, useCallback, RefObject } from 'react';
import useVideoStore from '@/stores/videoStore';
import { logger } from '@/services/loggerService';
import { errorReporter } from '@/services/errorReportingService';

interface UseVideoPlaybackOptions {
  onPlay?: () => void;
  isProcessing?: boolean;
  // Fires inside the processing-complete reset so callers can reload related
  // video elements (e.g. the hidden hover-preview thumbnail) alongside the
  // main videoRef — which is reloaded imperatively here. Keeps both elements
  // in sync when the signed URL string is unchanged across the refetch.
  onProcessingReset?: () => void;
}

interface UseVideoPlaybackReturn {
  // Refs
  videoRef: RefObject<HTMLVideoElement>;
  progressRef: RefObject<HTMLInputElement>;
  animationRef: RefObject<number>;
  // State
  isPlaying: boolean;
  isMetadataLoaded: boolean;
  hasStartedPlaying: boolean;
  duration: number;
  currentWidth: string;
  playbackRate: number;
  // Actions
  setIsPlaying: (playing: boolean) => void;
  setPlaybackRate: (rate: number) => void;
  handleLoadedMetadata: () => void;
  playOrPause: () => void;
  changeRange: () => void;
  handleChapterClick: (time: number) => void;
}

export function useVideoPlayback(options: UseVideoPlaybackOptions = {}): UseVideoPlaybackReturn {
  const { onPlay, isProcessing = false, onProcessingReset } = options;

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLInputElement>(null);
  const animationRef = useRef<number>(0);
  const prevIsProcessing = useRef(isProcessing);
  // Only allow processing reset if video was loaded while genuinely processing.
  // Prevents the false true→false transition on completed notes from resetting the video.
  const wasProcessingWithVideo = useRef(false);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentWidth, setCurrentWidth] = useState<string>('');
  const [playbackRate, setPlaybackRate] = useState<number>(1);

  // Video store
  const { setVideoRef, setProgressRef, setProgressTime, setVideoCurrentTime, setProgressCurrentTime } = useVideoStore();

  // Helper to update progress bar width
  const changePlayerCurrentTime = useCallback(() => {
    if (progressRef.current && duration > 0) {
      const time = Number(progressRef.current.value);
      setCurrentWidth(`${(time / duration) * 100}%`);
    }
  }, [duration]);

  // Animation frame callback for smooth playback tracking
  const whilePlaying = useCallback(() => {
    if (progressRef.current && videoRef.current) {
      setProgressTime();
      progressRef.current.value = videoRef.current.currentTime.toString();
      changePlayerCurrentTime();

      // Check if we're very close to the end but not quite there when at higher speeds
      const timeRemaining = videoRef.current.duration - videoRef.current.currentTime;
      if (playbackRate > 1 && timeRemaining > 0 && timeRemaining < 0.5) {
        // Ensure we don't miss the end at high speeds
        videoRef.current.currentTime = videoRef.current.duration - 0.1;
      }

      animationRef.current = requestAnimationFrame(whilePlaying);
    }
  }, [changePlayerCurrentTime, playbackRate, setProgressTime]);

  // Pause the video
  const onPause = useCallback(() => {
    if (videoRef.current && isMetadataLoaded) {
      videoRef.current.pause();
      setIsPlaying(false);
      cancelAnimationFrame(animationRef.current);
    }
  }, [isMetadataLoaded]);

  // Handle metadata loaded
  const handleLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    logger.debug('video', 'onLoadedMetadata fired', {
      videoWidth: v?.videoWidth ?? 0,
      videoHeight: v?.videoHeight ?? 0,
      duration: v?.duration ?? 0,
      readyState: v?.readyState ?? -1,
      networkState: v?.networkState ?? -1,
      srcLength: v?.src?.length ?? 0,
    });
    setIsMetadataLoaded(true);
    if (v) {
      const videoDuration = v.duration;
      setDuration(videoDuration);
      if (progressRef.current) {
        progressRef.current.max = videoDuration.toString();
      }
      setProgressTime();
    }
  }, [setProgressTime]);

  // Play or pause toggle
  const playOrPause = useCallback(() => {
    if (videoRef.current && isMetadataLoaded) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
        setHasStartedPlaying(true);
        // whilePlaying() intentionally absent — handlePlay event listener is the sole rAF entry point
      } else {
        onPause();
      }
    }
  }, [isMetadataLoaded, onPause]);

  // Handle progress bar change
  const changeRange = useCallback(() => {
    if (progressRef.current && videoRef.current) {
      const value = progressRef.current.value;
      setVideoCurrentTime(Number(value));
      changePlayerCurrentTime();
      if (videoRef.current.paused) {
        setProgressTime();
      }
    }
  }, [changePlayerCurrentTime, setProgressTime, setVideoCurrentTime]);

  // Handle chapter click
  const handleChapterClick = useCallback(
    (time: number) => {
      setVideoCurrentTime(time);
      setProgressCurrentTime(time);
      setProgressTime();
    },
    [setProgressCurrentTime, setProgressTime, setVideoCurrentTime],
  );

  // Sync refs with store
  useEffect(() => {
    setVideoRef(videoRef);
    setProgressRef(progressRef);
  }, [setVideoRef, setProgressRef]);

  // Update video playback rate when changed
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;

      // When playback rate changes and video is playing, ensure animation frame is updated
      if (isPlaying) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = requestAnimationFrame(whilePlaying);
      }
    }
  }, [playbackRate, isPlaying, whilePlaying]);

  // Track when video is genuinely loaded during active processing
  useEffect(() => {
    if (isProcessing && isMetadataLoaded) {
      wasProcessingWithVideo.current = true;
    }
  }, [isProcessing, isMetadataLoaded]);

  // Force video reload when processing genuinely completes
  useEffect(() => {
    logger.debug('video', 'useVideoPlayback isProcessing changed', {
      prev: prevIsProcessing.current,
      current: isProcessing,
      wasProcessingWithVideo: wasProcessingWithVideo.current,
      willReset: prevIsProcessing.current && !isProcessing && wasProcessingWithVideo.current,
    });
    if (prevIsProcessing.current && !isProcessing && wasProcessingWithVideo.current) {
      logger.debug('video', '⚠️ PROCESSING RESET — resetting metadata, reloading video element');
      wasProcessingWithVideo.current = false;
      // Cancel any running rAF and clear playing state before flipping isMetadataLoaded.
      // onPause guards on isMetadataLoaded, so the pause event fired by load() would skip
      // cancelAnimationFrame if it arrives after React applies the state reset.
      cancelAnimationFrame(animationRef.current);
      setIsPlaying(false);
      setIsMetadataLoaded(false);
      setHasStartedPlaying(false);
      setDuration(0);
      // Force the <video> element to re-fetch its current src. Do NOT append a
      // cache-buster query string — CDN signed URLs include query params in the
      // signature, so any mutation produces a 403 Forbidden.
      videoRef.current?.load();
      // Reload sibling video elements (e.g. hidden hover-preview thumbnail) so
      // they don't keep stale frames when the URL string is unchanged.
      onProcessingReset?.();
    }
    prevIsProcessing.current = isProcessing;
  }, [isProcessing, onProcessingReset]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setHasStartedPlaying(true);
      whilePlaying();
      onPlay?.();
    };

    const handleTimeUpdate = () => {
      if (video.paused && progressRef.current) {
        progressRef.current.value = video.currentTime.toString();
        changePlayerCurrentTime();
        setProgressTime();
      }
    };

    const handleEnded = () => {
      if (progressRef.current) {
        // When video ends, ensure progress bar shows complete
        progressRef.current.value = video.duration.toString();
        setCurrentWidth('100%');
        setProgressTime();
        setIsPlaying(false);
        cancelAnimationFrame(animationRef.current);
      }
    };

    const handleError = (e: Event) => {
      const target = e.target;
      if (target instanceof HTMLVideoElement && target.error) {
        errorReporter.expected('Video playback error', undefined, {
          feature: 'video',
          code: target.error.code,
          message: target.error.message,
        });
      }
    };

    // Add event listeners
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      video?.removeEventListener('play', handlePlay);
      video?.removeEventListener('pause', onPause);
      video?.removeEventListener('timeupdate', handleTimeUpdate);
      video?.removeEventListener('ended', handleEnded);
      video?.removeEventListener('error', handleError);
    };
  }, [changePlayerCurrentTime, onPause, onPlay, setProgressTime, whilePlaying]);

  return {
    // Refs
    videoRef,
    progressRef,
    animationRef,
    // State
    isPlaying,
    isMetadataLoaded,
    hasStartedPlaying,
    duration,
    currentWidth,
    playbackRate,
    // Actions
    setIsPlaying,
    setPlaybackRate,
    handleLoadedMetadata,
    playOrPause,
    changeRange,
    handleChapterClick,
  };
}
